import { EXPIRATION_TAG_MAX_DAYS, INSTRUMENT_VISIBILITY_MAP } from '../../../utils/constants'
import { pluralize } from '../../../utils/misc'
import moment from 'moment'

const DATE_FORMAT = 'DD MMM YYYY'
const TIME_FORMAT = 'h:mm'
const TIME_FORMAT_12H = 'h:mm A'
const TIME_FORMAT_24H = 'HH:mm'

const parseTime = timeStr => {
    if (!timeStr) return null

    const formats = [TIME_FORMAT_12H, TIME_FORMAT_24H, TIME_FORMAT]
    const parsed = moment(timeStr, formats, true)

    return parsed.isValid() ? parsed : null
}

const isDateTimeString = value =>
  typeof value === 'string' &&
  (value.includes('T') || /^\d{4}-\d{2}-\d{2}/.test(value))

export const parseVisibilityConfig = visibilityConfig => {
    if (
        visibilityConfig &&
        typeof visibilityConfig === 'object' &&
        visibilityConfig.visibility
    ) {
        return {
            visibilityType: visibilityConfig.visibility,
            ranges:
                visibilityConfig.visibility_ranges ||
                visibilityConfig.visibilityRanges ||
                []
        }
    }

    if (typeof visibilityConfig === 'string') {
        return {
            visibilityType: visibilityConfig,
            ranges: []
        }
    }

    return {
        visibilityType: null,
        ranges: []
    }
}

export const normalizeVisibilityConfig = (metadata = {}) => {
    const { visibility, visibilityRanges } = metadata

    if (visibility && typeof visibility === 'object' && visibility.visibility) {
        return visibility
    }

    if (visibility) {
        return {
            visibility,
            visibility_ranges: visibilityRanges || []
        }
    }

    return undefined
}

const isStartOfDay = momentObj =>
    momentObj.hours() === 0 &&
    momentObj.minutes() === 0 &&
    momentObj.seconds() === 0 &&
    momentObj.milliseconds() === 0

const getExpirationDayMoment = endMoment => {
    // Backend often stores same-day expiry as midnight at the start of the next day.
    if (isStartOfDay(endMoment)) {
        return endMoment.clone().subtract(1, 'day')
    }

    return endMoment.clone()
}

const isWithinTimeOnlyRange = (range, referenceTime = moment()) =>
  !!findActiveVisibilityRange([range], referenceTime)

export const isWithinVisibilityRange = (range, referenceTime = moment()) => {
  if (!range?.start || !range?.end) return true

  if (isDateTimeString(range.start) || isDateTimeString(range.end)) {
    const startMoment = moment(range.start)
    const endMoment = moment(range.end)
    if (!startMoment.isValid() || !endMoment.isValid()) return true
    const ref = moment(referenceTime)
    return ref.isSameOrAfter(startMoment) && ref.isSameOrBefore(endMoment)
  }

  const startTime = parseTime(range.start)
  const endTime = parseTime(range.end)
  if (!startTime || !endTime) return true

  return isWithinTimeOnlyRange(range, referenceTime)
}

const hasTimedVisibility = visibilityType =>
  visibilityType === INSTRUMENT_VISIBILITY_MAP.specificTime ||
  visibilityType === INSTRUMENT_VISIBILITY_MAP.custom

export const isVisibleByConfig = (visibilityConfig, referenceTime = moment()) => {
  const { visibilityType, ranges } = parseVisibilityConfig(visibilityConfig)

  if (!visibilityType || visibilityType === INSTRUMENT_VISIBILITY_MAP.allDay) {
    return true
  }

  if (!hasTimedVisibility(visibilityType)) {
    return true
  }

  if (!ranges?.length) return true

  return ranges.some(range => isWithinVisibilityRange(range, referenceTime))
}

export const isVisibleOnCalendarDay = (
  visibilityConfig,
  targetDayMoment,
  { isToday = false } = {},
) => {
  const { visibilityType, ranges } = parseVisibilityConfig(visibilityConfig)

  if (!visibilityType || visibilityType === INSTRUMENT_VISIBILITY_MAP.allDay) {
    return true
  }

  if (!ranges?.length) return true

  const usesDateTimeRanges = ranges.some(
    range => isDateTimeString(range.start) || isDateTimeString(range.end),
  )

  if (visibilityType === INSTRUMENT_VISIBILITY_MAP.custom || usesDateTimeRanges) {
    return ranges.some(range => {
      const start = moment(range.start)
      const end = moment(range.end)
      if (!start.isValid() || !end.isValid()) return false

      const dayStart = targetDayMoment.clone().startOf('day')
      const dayEnd = targetDayMoment.clone().endOf('day')
      if (!start.isSameOrBefore(dayEnd) || !end.isSameOrAfter(dayStart)) {
        return false
      }

      if (isToday) {
        return isWithinVisibilityRange(range, moment())
      }

      return true
    })
  }

  if (visibilityType === INSTRUMENT_VISIBILITY_MAP.specificTime) {
    if (!isToday) return true
    return ranges.some(range => isWithinTimeOnlyRange(range, moment()))
  }

  return true
}

const findActiveVisibilityRange = (ranges, referenceTime = moment()) => {
    const currentTime = moment()
        .hours(referenceTime.hours())
        .minutes(referenceTime.minutes())
        .seconds(0)
        .milliseconds(0)

    for (const range of ranges) {
        const startTime = parseTime(range.start)
        const endTime = parseTime(range.end)

        if (!startTime || !endTime) continue

        const startTimeToday = moment()
            .hours(startTime.hours())
            .minutes(startTime.minutes())
            .seconds(0)
            .milliseconds(0)
        const endTimeToday = moment()
            .hours(endTime.hours())
            .minutes(endTime.minutes())
            .seconds(0)
            .milliseconds(0)

        if (endTimeToday.isBefore(startTimeToday)) {
            if (
                currentTime.isSameOrAfter(startTimeToday) ||
                currentTime.isSameOrBefore(endTimeToday)
            ) {
                return { range, endsNextDay: true }
            }
        } else if (
            currentTime.isSameOrAfter(startTimeToday) &&
            currentTime.isSameOrBefore(endTimeToday)
        ) {
            return { range, endsNextDay: false }
        }
    }

    return null
}

const adjustEndMomentForVisibility = (endMoment, visibilityConfig) => {
    const { visibilityType, ranges } = parseVisibilityConfig(visibilityConfig)

    if (
        !hasTimedVisibility(visibilityType) ||
        !ranges?.length
    ) {
        return endMoment
    }

    const activeRangeInfo = findActiveVisibilityRange(ranges)
    const rangeToUse = activeRangeInfo?.range || ranges[ranges.length - 1]
    const endTime = parseTime(rangeToUse?.end)

    if (!endTime) return endMoment

    const expirationDay = getExpirationDayMoment(endMoment)

    const adjustedEnd = expirationDay
        .clone()
        .hours(endTime.hours())
        .minutes(endTime.minutes())
        .seconds(0)
        .milliseconds(0)

    if (activeRangeInfo?.endsNextDay) {
        adjustedEnd.add(1, 'day')
    }

    return adjustedEnd
}

export const getExpirationText = (schedule = {}, instances = [], visibility) => {

    const { expire } = schedule

    if (expire?.absolute && expire?.supText) {
        const isToday = moment.unix(expire.absolute).format(DATE_FORMAT) === moment().format(DATE_FORMAT);
        return isToday ? 'Expire: End of Day' : `Expire: ${expire.supText}`;
    }

    const { end_date: endDate } = instances?.[0] || {}

    if (!endDate) {
        return
    }

    const now = moment();
    const endMoment = adjustEndMomentForVisibility(moment(endDate), visibility)
    const duration = moment.duration(endMoment.diff(now));

    if (duration.asMilliseconds() <= 0) {
        if (expire?.supText) {
            return `Expire: ${expire.supText}`
        }
        return
    }

    const interval = expire?.relative_to_send_ideal?.interval;
    const wholeDays =
        interval !== undefined ? interval - 1 : Math.floor(duration.asDays());

    if (wholeDays > EXPIRATION_TAG_MAX_DAYS) {
        return
    }

    const minutes = duration.minutes();
    const seconds = duration.seconds();

    if (wholeDays >= 1) {
        const remainderMs =
            duration.asMilliseconds() - wholeDays * 24 * 60 * 60 * 1000
        const extraHours = Math.floor(
            moment.duration(Math.max(0, remainderMs)).asHours(),
        )

        if (extraHours >= 1) {
            return `Expire: in ${pluralize(wholeDays, 'day')} ${pluralize(extraHours, 'hour')}`
        }

        return `Expire: in ${pluralize(wholeDays, 'day')}`
    }

    const totalHours = Math.floor(duration.asHours());
    if (totalHours >= 1) {
        return `Expire: in ${pluralize(totalHours, 'hour')} ${pluralize(minutes, 'minute')}`;
    }

    return `Expire: in ${pluralize(minutes, 'minute')} ${pluralize(seconds, 'second')}`;
}
